import * as React from 'react'
import { Loader2 } from 'lucide-react'
import {
  isMainBranch,
  useCreateBranchMutation,
  useListBranchesQuery,
  useSwitchBranch
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select'

const BASELINE_REF = 'main'

function extractApiError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: unknown }).data
    if (data && typeof data === 'object' && 'message' in data) {
      const m = (data as { message?: unknown }).message
      if (typeof m === 'string' && m.length > 0) return m
    }
  }
  return fallback
}

/**
 * Create-branch card for the Branches settings page. Forks a new branch off
 * main or a chosen user branch at its current head (an O(1) pointer insert) and
 * opens it as the active branch. Fork candidates are the first page of
 * user-facing branches — the same set the merge/promote dialogs offer.
 */
export function CreateBranchCard() {
  const { data } = useListBranchesQuery({ origin: 'user' })
  const forkSources = React.useMemo(
    () => (data?.branches ?? []).filter((b) => b.status === 'active' && !isMainBranch(b)),
    [data]
  )
  const [createBranch, createState] = useCreateBranchMutation()
  const { switchBranch } = useSwitchBranch()

  const [name, setName] = React.useState('')
  const [sourceId, setSourceId] = React.useState<string>(BASELINE_REF)
  const [submitError, setSubmitError] = React.useState('')

  const trimmed = name.trim()
  const canSubmit = trimmed.length > 0 && !createState.isLoading

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit) return
    setSubmitError('')
    try {
      const created = await createBranch({
        name: trimmed,
        ...(sourceId !== BASELINE_REF ? { sourceId } : {})
      }).unwrap()
      // Opening the new branch builds its working database on first open.
      void switchBranch(created.id).catch(() => {
        setSubmitError(`Created ${created.name}, but could not open it.`)
      })
      setName('')
      setSourceId(BASELINE_REF)
    } catch (err) {
      setSubmitError(extractApiError(err, 'Could not create branch.'))
    }
  }

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-ink">New branch</h3>

        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          <div className="flex flex-row items-center gap-2">
            <Input
              aria-label="New branch name"
              placeholder="branch-name"
              value={name}
              onChange={(event) => {
                setName(event.target.value)
                setSubmitError('')
              }}
              className="font-mono"
            />
            <span className="text-xs text-ink-3">from</span>
            <Select value={sourceId} onValueChange={setSourceId}>
              <SelectTrigger size="sm" aria-label="Base branch">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BASELINE_REF}>main</SelectItem>
                {forkSources.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" size="xs" variant="outline" disabled={!canSubmit}>
              {createState.isLoading ? <Loader2 className="animate-spin" /> : null}
              Create
            </Button>
          </div>

          {submitError ? (
            <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
              {submitError}
            </div>
          ) : null}
        </form>

        <p className="text-xs text-ink-3">
          Forks from the chosen base at its current head and opens the new branch — building its
          working copy on first open (a few seconds).
        </p>
      </CardContent>
    </Card>
  )
}
